import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const VerifyReviewer = () => {
  const [reviewers, setReviewers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalFileUrl, setModalFileUrl] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const API = process.env.REACT_APP_API_BASE_URL;
  const platformId = localStorage.getItem('platformID');

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      toolbarSlot: (slots) => {
        const { Download, ...rest } = slots;
        return { ...rest };
      },
    },
  });

  useEffect(() => {
    fetchReviewers();
  }, []);

  const fetchReviewers = async () => {
    try {
      const res = await axios.get(`${API}/api/reviewer/unapproved/${platformId}`);
      setReviewers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleApprove = async () => {
    try {
      const res = await axios.put(`${API}/api/reviewer/approve`, {
        reviewerIDs: selectedIds
      });
      alert(res.data.message);
      setSelectedIds([]);
      fetchReviewers();
    } catch (err) {
      console.error(err);
      alert('Error approving reviewers');
    }
  };

  const handlePending = async () => {
    try {
      const res = await axios.put(`${API}/api/reviewer/pending`, {
        reviewerIDs: selectedIds
      });
      alert(res.data.message);
      setSelectedIds([]);
      fetchReviewers();
    } catch (err) {
      console.error(err);
      alert('Error approving reviewers');
    }
  };

  // Reject Modal Handlers
  const openRejectModal = () => {
    if (selectedIds.length === 0) return;
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setRejectReason('');
    setShowRejectModal(false);
  };

  const submitRejectReason = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      const res = await axios.put(`${API}/api/reviewer/reject`, {
        reviewerIDs: selectedIds,
        reason: rejectReason.trim()
      });
      alert(res.data.message);
      setSelectedIds([]);
      setRejectReason('');
      closeRejectModal();
      fetchReviewers();
    } catch (err) {
      console.error(err);
      alert('Error rejecting reviewers');
    }
  };

  const openModal = (fileName, fileType = 'agreement') => {
    // console.log("Opening modal for:", fileName, "Type:", fileType);

    let folder = 'agreement'; // default

    if (fileType?.toLowerCase() === 'verification') {
      folder = 'verification';
    } else if (fileType?.toLowerCase() === 'profile') {
      folder = 'profile';
    }

    setModalFileUrl(`${API}/uploads/${folder}/${fileName}`);
    setShowModal(true);
  };

  const closeModal = () => {
    setModalFileUrl('');
    setShowModal(false);
  };

  return (
    <div className="container my-4 ml-2">
      <h3 className="page-title mb-4">Verify Reviewers</h3>
      {reviewers.length === 0 ? (
        <div className="alert alert-info fw-bold text-center mb-3 rounded shadow-sm" role="alert">
          No unapproved reviewers found.
        </div>
      ) : (
        <>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Select</th>
                <th>ReviewerID</th>
                <th>Name</th>
                <th>User Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Identification File</th>
                <th>Profile</th>
              </tr>
            </thead>
            <tbody>
              {reviewers.map((reviewer) => (
                <tr key={reviewer.ReviewerID}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(reviewer.ReviewerID)}
                      onChange={() => handleCheckbox(reviewer.ReviewerID)}
                    />
                  </td>
                  <td>{reviewer.ReviewerID}</td>
                  <td>{reviewer.ReviewerName}</td>
                  <td>{reviewer.UserName}</td>
                  <td>{reviewer.EmailID}</td>
                  <td>{reviewer.ContactNumber}</td>
                  <td>{reviewer.Address}</td>
                  <td>
                    {reviewer.AgreementFile && reviewer.AgreementFile.endsWith('.pdf') ? (
                      <button
                        className="btn btn-info sm"
                        onClick={() => openModal(`${reviewer.AgreementFile}`)}
                      >
                        View PDF
                      </button>
                    ) : (
                      <div className="alert alert-warning p-1 m-0">Invalid file</div>
                    )}
                  </td>
                  <td>
                    {reviewer.Profile && reviewer.Profile.endsWith('.pdf') ? (
                      <button
                        className="btn btn-info sm"
                        onClick={() => openModal(`${reviewer.Profile}`, 'profile')}
                      >
                        View PDF
                      </button>
                    ) : (
                      <div className="alert alert-warning p-1 m-0">Invalid file</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button
            className="btn btn-success mb-2"
            onClick={handleApprove}
            disabled={selectedIds.length === 0}
          >
            Approve
          </button>
          <button
            className="btn btn-warning mb-2 ml-2"
            onClick={handlePending}
            disabled={selectedIds.length === 0}
          >
            Pending
          </button>
          <button
            className="btn btn-danger mb-2 ml-2"
            onClick={openRejectModal}
            disabled={selectedIds.length === 0}
          >
            Reject
          </button>
        </>
      )}

      {/* Modal for PDF Viewer */}
      {showModal && (
        <div className="modal show fade d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">PDF Preview</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body" style={{ height: '80vh' }}>
                <Worker workerUrl="/pdf.worker.min.js">
                  <Viewer
                    fileUrl={modalFileUrl}
                    plugins={[defaultLayoutPluginInstance]}
                  />
                </Worker>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={closeRejectModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reason for Rejection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejecting selected reviewers"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRejectModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submitRejectReason}>
            Reject Reviewers
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VerifyReviewer;
